import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { avatarDef, HERO_AVATARS, type HeroAvatar } from "@/content/heroes";
import { levelLabel, lt, t } from "@/i18n";
import { LEVEL_IDS, topicsForLevel, type AnswerPreference, type LevelId } from "@/math";
import {
  cloudAvailable,
  cloudBusy,
  cloudUser,
  deleteCloudSave,
  loginUrl,
  LOGOUT_URL,
  syncFromCloud,
} from "@/services/cloud";
import { listProfiles, type ProfileMeta } from "@/state/profiles";
import {
  deleteSave,
  importSave,
  loadGame,
  readProfileTopics,
  startNewGame,
  updateProfileTopics,
} from "@/state/save";
import { defaultHero, hero, lang, panel, screen, settings, showToast } from "@/state/store";
import { TopicPicker } from "./TopicPicker";

const allTopicIds = (level: LevelId) => new Set(topicsForLevel(level).map((td) => td.id));

export function StartPanel() {
  const profiles = useSignal<ProfileMeta[]>(listProfiles());
  const creating = useSignal(profiles.value.length === 0);
  const confirmDelete = useSignal<string | null>(null);
  const editing = useSignal<ProfileMeta | null>(null);
  const L = lang.value;

  // pull heroes saved in the cloud (when logged in) once the start screen opens
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!cloudUser.value) return;
      const n = await syncFromCloud(profiles.value, importSave);
      if (cancelled) return;
      profiles.value = listProfiles();
      if (profiles.value.length > 0) creating.value = false;
      if (n > 0) showToast(t("cloud.synced", { n }));
    })();
    return () => {
      cancelled = true;
    };
  }, [cloudUser.value]);

  const play = (id: string) => {
    if (loadGame(id)) screen.value = "world";
  };
  const remove = (id: string) => {
    deleteSave(id);
    if (cloudUser.value) void deleteCloudSave(id);
    confirmDelete.value = null;
    profiles.value = listProfiles();
    if (profiles.value.length === 0) creating.value = true;
  };
  const showCurriculum = (level: LevelId) => {
    // the curriculum report uses the hero's level; preview it for the selected level
    hero.value = { ...hero.value, level };
    panel.value = "report-curriculum";
  };

  return (
    <div class="panel start">
      <h1>MathRPG</h1>
      <h2>{L === "pl" ? "Rozdarta Księga" : "The Torn Ledger"}</h2>
      <p class="tagline">{t("title.tagline")}</p>

      <label>
        <span>{t("start.language")}</span>
        <div class="segmented">
          <button class={L === "pl" ? "on" : ""} onClick={() => (lang.value = "pl")}>
            Polski
          </button>
          <button class={L === "en" ? "on" : ""} onClick={() => (lang.value = "en")}>
            English
          </button>
        </div>
      </label>

      {editing.value ? (
        <ProfileTopicsEditor
          profile={editing.value}
          onDone={() => {
            editing.value = null;
            profiles.value = listProfiles();
          }}
        />
      ) : (
        <>
          {!creating.value && (
            <div class="profiles">
              <div class="section-title">{t("start.heroes")}</div>
              {profiles.value.map((p) => (
                <div key={p.id} class="profile-row">
                  <img class="faceset small" src={avatarDef(p.avatar).faceset} alt="" />
                  <div class="info">
                    <div class="name">
                      {p.name} · {t("hud.lv")} {p.lvl}
                    </div>
                    <div class="meta">
                      {levelLabel(p.level)} · {t("start.lastPlayed")} {formatDate(p.updatedAt, L)}
                    </div>
                  </div>
                  {confirmDelete.value === p.id ? (
                    <>
                      <button class="danger" onClick={() => remove(p.id)}>
                        {t("start.confirmDelete")}
                      </button>
                      <button onClick={() => (confirmDelete.value = null)}>{t("start.cancel")}</button>
                    </>
                  ) : (
                    <>
                      <button class="primary" onClick={() => play(p.id)}>
                        ▶ {t("start.play")}
                      </button>
                      <button
                        class="subtle"
                        title={t("start.topics.edit")}
                        onClick={() => (editing.value = p)}
                      >
                        ⚙
                      </button>
                      <button
                        class="subtle"
                        title={t("start.delete")}
                        onClick={() => (confirmDelete.value = p.id)}
                      >
                        🗑
                      </button>
                    </>
                  )}
                </div>
              ))}
              <div class="actions">
                <button onClick={() => (creating.value = true)}>➕ {t("start.newHero")}</button>
              </div>
            </div>
          )}

          {creating.value && (
            <NewHeroForm
              canCancel={profiles.value.length > 0}
              onCancel={() => (creating.value = false)}
              onCurriculum={showCurriculum}
              onCreate={(name, level, avatar, topics) => {
                const h = defaultHero(name, level, avatar);
                if (topics) h.topics = topics;
                startNewGame(h);
                screen.value = "world";
              }}
            />
          )}
        </>
      )}

      {!creating.value && (
        <div class="actions secondary">
          <button onClick={() => (panel.value = "help")}>❓ {t("start.help")}</button>
        </div>
      )}
      <CloudBox />
      <p class="hint">{t("start.hint")}</p>
    </div>
  );
}

function NewHeroForm(props: {
  canCancel: boolean;
  onCancel: () => void;
  onCurriculum: (level: LevelId) => void;
  onCreate: (name: string, level: LevelId, avatar: HeroAvatar, topics: string[] | null) => void;
}) {
  const name = useSignal("");
  const level = useSignal<LevelId>("sp6");
  const avatar = useSignal<HeroAvatar>("boy");
  const topics = useSignal<Set<string>>(allTopicIds("sp6"));
  const pickTopics = useSignal(false);
  const L = lang.value;
  const total = topicsForLevel(level.value).length;
  const create = () => {
    const chosen = topics.value.size > 0 && topics.value.size < total ? [...topics.value] : null;
    props.onCreate(name.value.trim() || (L === "pl" ? "Bohater" : "Hero"), level.value, avatar.value, chosen);
  };

  return (
    <div
      class="new-hero"
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target instanceof HTMLInputElement && e.target.type === "text") create();
      }}
    >
      <div class="section-title">{t("start.newHero")}</div>
      <label>
        <span>{t("start.avatar")}</span>
        <div class="avatar-pick">
          {(Object.values(HERO_AVATARS) as Array<(typeof HERO_AVATARS)[HeroAvatar]>).map((a) => (
            <button
              key={a.id}
              class={avatar.value === a.id ? "on" : ""}
              onClick={() => (avatar.value = a.id)}
              title={lt(a.name)}
            >
              <img class="faceset" src={a.faceset} alt="" />
              <span>{lt(a.name)}</span>
            </button>
          ))}
        </div>
      </label>

      <label>
        <span>{t("start.name")}</span>
        <input
          type="text"
          maxLength={20}
          value={name.value}
          placeholder={L === "pl" ? "Bohater" : "Hero"}
          onInput={(e) => (name.value = (e.currentTarget as HTMLInputElement).value)}
        />
      </label>

      <label>
        <span>{t("start.level")}</span>
        <select
          value={level.value}
          onChange={(e) => {
            level.value = (e.currentTarget as HTMLSelectElement).value as LevelId;
            topics.value = allTopicIds(level.value);
          }}
        >
          {LEVEL_IDS.map((id) => {
            const available = topicsForLevel(id).length > 0;
            return (
              <option value={id} disabled={!available}>
                {levelLabel(id)}
                {available ? "" : ` (${t("start.soon")})`}
              </option>
            );
          })}
        </select>
      </label>

      <label>
        <span>{t("start.topics")}</span>
        <div class="segmented">
          <button class={!pickTopics.value ? "on" : ""} onClick={() => (pickTopics.value = false)}>
            {topics.value.size === total
              ? t("start.topics.allShort")
              : t("start.topics.summary", { n: topics.value.size, total })}
          </button>
          <button class={pickTopics.value ? "on" : ""} onClick={() => (pickTopics.value = !pickTopics.value)}>
            {t("start.topics.choose")}
          </button>
        </div>
      </label>
      {pickTopics.value && (
        <TopicPicker level={level.value} selected={topics.value} onChange={(next) => (topics.value = next)} />
      )}

      <label>
        <span>{t("start.answerMode")}</span>
        <select
          value={settings.value.preference}
          onChange={(e) =>
            (settings.value = {
              ...settings.value,
              preference: (e.currentTarget as HTMLSelectElement).value as AnswerPreference,
            })
          }
        >
          <option value="auto">{t("start.answerMode.auto")}</option>
          <option value="choice">{t("start.answerMode.choice")}</option>
          <option value="typed">{t("start.answerMode.typed")}</option>
        </select>
      </label>

      <label>
        <span>{t("start.timer")}</span>
        <select
          value={String(settings.value.timerSeconds)}
          onChange={(e) =>
            (settings.value = {
              ...settings.value,
              timerSeconds: Number((e.currentTarget as HTMLSelectElement).value),
            })
          }
        >
          <option value="0">{t("start.timer.off")}</option>
          <option value="20">20 s</option>
          <option value="30">30 s</option>
          <option value="45">45 s</option>
          <option value="60">60 s</option>
        </select>
      </label>

      <label>
        <span>{t("start.calculator")}</span>
        <div class="segmented">
          <button
            class={settings.value.calculator ? "on" : ""}
            onClick={() => (settings.value = { ...settings.value, calculator: true })}
          >
            {t("start.yes")}
          </button>
          <button
            class={!settings.value.calculator ? "on" : ""}
            onClick={() => (settings.value = { ...settings.value, calculator: false })}
          >
            {t("start.no")}
          </button>
        </div>
      </label>

      <div class="actions">
        <button class="primary" onClick={create}>
          {t("start.create")}
        </button>
        {props.canCancel && <button onClick={props.onCancel}>{t("start.back")}</button>}
      </div>
      <div class="actions secondary">
        <button onClick={() => props.onCurriculum(level.value)}>📄 {t("start.curriculum")}</button>
        <button onClick={() => (panel.value = "help")}>❓ {t("start.help")}</button>
      </div>
    </div>
  );
}

/** Change which topics a saved hero is quizzed on (parents adjust it as the school year goes on). */
function ProfileTopicsEditor(props: { profile: ProfileMeta; onDone: () => void }) {
  const level = props.profile.level;
  const saved = readProfileTopics(props.profile.id);
  const topics = useSignal<Set<string>>(saved && saved.length > 0 ? new Set(saved) : allTopicIds(level));
  const total = topicsForLevel(level).length;
  const save = () => {
    const chosen = topics.value.size > 0 && topics.value.size < total ? [...topics.value] : null;
    updateProfileTopics(props.profile.id, chosen);
    showToast(t("start.topics.saved", { name: props.profile.name }));
    props.onDone();
  };
  return (
    <div class="new-hero">
      <div class="section-title">
        {t("start.topics.edit")}: {props.profile.name} · {levelLabel(level)}
      </div>
      <TopicPicker level={level} selected={topics.value} onChange={(next) => (topics.value = next)} />
      <div class="actions">
        <button class="primary" onClick={save}>
          {t("start.topics.save")}
        </button>
        <button onClick={props.onDone}>{t("start.cancel")}</button>
      </div>
    </div>
  );
}

/** Login / logout for cloud saves; hidden when the site does not run on Azure Static Web Apps. */
function CloudBox() {
  if (!cloudAvailable.value) return null;
  const user = cloudUser.value;
  return (
    <div class="cloud">
      <span class="section-title">☁ {t("cloud.title")}</span>
      {user ? (
        <>
          <span>{cloudBusy.value ? t("cloud.syncing") : t("cloud.loggedIn", { name: user.name })}</span>
          <a class="button" href={LOGOUT_URL}>
            {t("cloud.logout")}
          </a>
        </>
      ) : (
        <>
          <span>{t("cloud.login")}</span>
          <a class="button" href={loginUrl("aad")}>
            Microsoft
          </a>
          <a class="button" href={loginUrl("github")}>
            GitHub
          </a>
        </>
      )}
    </div>
  );
}

function formatDate(iso: string, L: "pl" | "en"): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(L === "pl" ? "pl-PL" : "en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}
